import React, { useContext } from 'react'
import { Link } from 'gatsby'
import ExternalLink from 'components/external-link.js'
import { useTheme } from '@emotion/react'
import AppContext from 'context/app'
import Container from 'components/container.js'
import SearchInput from 'components/search/input'
import logo from 'svgs/logo-rane-white.svg'
import accountIcon from 'svgs/account-icon-white.svg'
import hamburgerIcon from 'svgs/hamburger-icon-white.svg'
import xCloseIcon from 'svgs/x-close-white.svg'
import { ReactComponent as ChevronDown } from 'svgs/chevron-down-white.svg'
import { useSearchBox } from 'react-instantsearch'
import paths from 'data/paths.json'
import chevronRight from 'svgs/chevron-right-black.svg'

const delayOpen = (e, setOverlay) => {
    if(typeof window === `undefined`) return
    
    // in case anything other than <a> is hovered (like img or ::after)
    const el = e.target.closest('a')
    let overlayTimeout

    const clearOverlayTimeout = () => {
        clearTimeout(overlayTimeout)
        window.removeEventListener('navigate', clearOverlayTimeout)
        el.removeEventListener('mouseleave', clearOverlayTimeout)
    }

    window.addEventListener('navigate', clearOverlayTimeout)
    el.addEventListener('mouseleave', clearOverlayTimeout)

    overlayTimeout = setTimeout(() => {
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
        if (el.matches(':hover')) setOverlay(el.text)        
        window.removeEventListener('navigate', clearOverlayTimeout)
        el.removeEventListener('mouseleave', clearOverlayTimeout)
    }, 300)
}

const CATEGORIES = [
    {
        name: 'Products',
        url: paths.root.products.path,
        overlay: true,
    },
    {
        name: 'Downloads',
        url: paths.root.downloads.path,
    },
    {
        name: 'Artists',
        url: paths.root.artists.path,
    },
    {
        name: 'Forum',
        url: paths.external.forum,
        external: true,
    },
    {
        name: 'Support',
        url: paths.external.support,
        external: true,
    },
]

export const Categories = (props) => {
    const { overlay, setOverlay, setSubmenu } = useContext(AppContext)
    const { isMobile } = props
    const theme = useTheme()

    return (
        <div {...props}>
            {CATEGORIES.map((category, i) => {
                const { name, url, external } = category
                
                const Element = external ? ExternalLink : Link

                const styles = {
                    color: overlay === name ? theme.colors.primary : 'white',
                }

                if(i === 0 && overlay === name){
                    /**
                     * Add some blank space before the first category
                     * so that if user is moving mouse in the south-west direction
                     * (after hovering the first option and opening the overlay)
                     * they dont accidently close the nav overlay
                     */
                    styles['::before'] = {
                        display: 'block',
                        content: `""`,
                        position: 'absolute',
                        top: 0,
                        left: -128,
                        width: 128,
                        height: '100%',
                        cursor: 'auto',
                    }
                }
                
                if(external){
                    styles['::after'] = {
                        content: 'url(/svg/external-link-white.svg)',
                        position: 'relative',
                        bottom: 4,
                        left: 2,
                    }
                    styles[':hover::after'] = {
                        content: 'url(/svg/external-link-primary.svg)'
                    }
                }
                
                if(isMobile){
                    if(category?.overlay) {
                        return(
                            <button
                                key={`mobile-category-${i}`}
                                className='d-flex j-between a-center p-4 b-0 bg-0 pointer h4 sm black'
                                onClick={() => setSubmenu(name)}
                            >
                                {name}
                                <img src={chevronRight} alt=""/>
                            </button>
                        )
                    } else {
                        const to = external ? url : `/${name.toLowerCase()}`
                        return(
                            <Element to={to} className='p-4 b-0 bg-0 pointer h4 sm black'>
                                {name}
                            </Element>
                        )
                    }
                }

                return(
                    <Element
                        key={`category-${i}`}
                        to={url}
                        css={styles}
                        className='td-0 h-primary'
                        onMouseEnter={i === 0 ?
                            e => delayOpen(e, setOverlay) :
                            () => setOverlay('')
                        }
                    >
                        {name}
                        {i === 0 && <ChevronDown
                            style={{ transition: 'all .3s ease'}}
                            css={
                                overlay === name ?
                                {
                                    transform: 'rotate(-90deg)',
                                    '& path': { fill: theme.colors.primary },
                                } :
                                {
                                    '& path': { fill: 'white' }
                                }
                            }
                        />}
                    </Element>
                )
            })}
        </div>
    )
}

export default function NavBar(){
    const {
        overlay,
        setOverlay,
        closeOverlay,
        subMenu,
        setSubmenu,
        clientWidth,
    } = useContext(AppContext)

    const { clear } = useSearchBox()

    const isSearch = overlay === 'Search'
    const isAccount = overlay === 'Account'
    const isSubMenuAccount = subMenu === 'Account'
    const isMobile = overlay === 'Mobile'

    return(
        <Container
            fluid
            id='navbar'
            onMouseMove={e => {
                if(!overlay || isSearch || isAccount || isMobile) return
                if(e.target.closest('#navbar-category-links')) return
                closeOverlay()
            }}
        >
            <Container>
                <div
                    className='d-flex j-between a-stretch'
                    css={{
                        padding: '0px 32px',
                        '@media(max-width: 1440px)': {
                            padding: '0px 20px',
                        }
                    }}
                >
                    {/* LOGO */}
                    <Link
                        to={paths.root.path}
                        css={{
                            paddingTop: 16,
                            paddingBottom: 16,
                            paddingRight: 10,
                            '@media(max-width: 425px)': {
                                display: isSearch ? 'none' : 'inline-block'
                            }
                        }}
                    >
                        <img src={logo} height="24" alt="RANE" />
                    </Link>
                    
                    {/* CATEGORIES */}
                    {!isSearch && (
                        <Categories
                            id='navbar-category-links'
                            css={{
                                display: 'flex',
                                gap: 40,
                                '@media(max-width: 1024px)': {
                                    display: 'none',
                                }
                            }}
                        />
                    )}
                    
                    {/* SEARCH | ACCOUNT | X | HAMBURGER */}
                    <div
                        style={isSearch ? { flexGrow: 1 } : { flexGrow: 0 } }
                        className='d-flex a-stretch shrink-1'
                        css={{                            
                            position: 'relative',
                            /**
                             * Above 1024px the account icon is the right-most button in the navbar.
                             * Currently it is an svg icon with a width of 28px.
                             * However inherent in the image is 5px of padding on each side.
                             * So the total width of the actual icon is actually 28 - 10 = 18.
                             * 
                             * The button that contains that image is fixed at 48px.
                             * Which means the total white space in the button is 48 - 18 = 30.
                             * 
                             * To pull this section's content so that the right hand side aligns with
                             * subsequent content on the page then we must pull this section of the navbar
                             * to the right half of the total white space of that button aka the right side
                             * which is 30 / 2 = 15
                             */
                            right: -15,
                            /**
                             * Below 1024px the hamburger icon is the right-most button in the navbar.
                             * Currently it is an svg icon with a width of 28px.
                             * However inherent in the image is 4px of padding on each side.
                             * So the total width of the actual icon is actually 28 - 8 = 20.
                             * 
                             * The button that contains that image is fixed at 48px.
                             * Which means the total white space in the button is 48 - 20 = 28.
                             * 
                             * To pull this section's content so that the right hand side aligns with
                             * subsequent content on the page then we must pull this section of the navbar
                             * to the right half of the total white space of that button aka the right side
                             * which is 28 / 2 = 14
                             */
                            '@media(max-width: 1024px)': { right: -14 },
                        }}
                    >
                        {/* SEARCH */}
                        <SearchInput />
    
                        {/* ACCOUNT */}
                        {(!isSearch && !isAccount && !isSubMenuAccount) && <button
                            onClick={() => {
                                if(clientWidth < 1024){
                                    setOverlay('Mobile')
                                    setSubmenu('Account')
                                } else {
                                    setOverlay('Account')
                                }
                            }}
                            css={{
                                padding: 0,
                                minWidth: 48,
                                background: 'transparent',
                                border: 'none',
                                '&:hover': {
                                    cursor: 'pointer',
                                }
                            }}
                        >
                            <img src={accountIcon} alt="Account" />
                        </button>}
    
                        {/* X */}
                        {(isSearch || isAccount || isMobile) && (
                            <button
                                onClick={() => {
                                    if(isSearch) clear()
                                    closeOverlay()
                                }}
                                className='p-0 bg-0 b-0 pointer'
                                css={{ minWidth: 48 }}
                            >
                                <img src={xCloseIcon} alt='Mobile menu' />
                            </button>
                        )}
    
                        {/* HAMBURGER */}
                        {(isSubMenuAccount || (!isMobile && !isSearch)) && (
                            <button
                                id='hamburger'
                                onClick={() => {
                                    if(isSubMenuAccount) setSubmenu('')
                                    else setOverlay('Mobile')
                                }}
                                className='p-0 bg-0 b-0 pointer'
                                css={{
                                    minWidth: 48,
                                    '@media(min-width: 1025px)': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <img
                                    src={hamburgerIcon}
                                    alt='Mobile menu'
                                />
                            </button>
                        )}
                    </div>
                </div>
            </Container>
        </Container>
    )
}